import { PREVIEW_FEATURE_NAME } from "@/components/r3f/renderers/preview-feature-renderer";
import { MARKUP_MODEL_LABEL } from "@/components/r3f/renderers/zip-model-3d-renderer";
import {
  isObjLine,
  isObjOdometryPath,
  isObjPlaceholders,
} from "@/modes/overview-mode/render-dispatch-utils";
import { CadModelObject, PointCloudObject } from "@/object-cache";
import {
  isPanoObject,
  isPhotoObject,
  isSheetObject,
  isUnknownObject,
} from "@/object-cache-type-guard";
import { MANUAL_BLUR_LABEL } from "@/tools/manual-blurring-tool/manual-blurring-types";
import {
  GRID_PLANE_NAME,
  isExplorationPivot,
} from "@faro-lotv/app-component-toolbox";
import { assert } from "@faro-lotv/foundation";
import { BOX_CONTROLS_NAME } from "@faro-lotv/lotv";
import { SparkRenderer, SplatMesh } from "@faro-lotv/spark-gaussian-splats";
import { Mesh, Object3D } from "three";
import { GUIDED_NAVIGATION_GUIDES_NAME } from "../renderers/guided-navigation-materials";
import { ZOOM_LENS_FRAME } from "./use-zoom-lens";

/**
 * Tagging enum type to label all steps of the rendering pipeline, so that every object
 * can be correctly dispatched to the appropriate pass in the pipeline..
 */
export enum RenderDispatch {
  LodPointCloudPass = 0,
  CadModelPass = 1,
  FloorPlanPass = 2,
  OpaquePass = 3,
  PreviousImagePass = 4,
  PanoPass = 5,
  CosmeticsPass = 6,
  GaussianSplatsPass = 7,
  /** Explicitly defining NoPass type when an object should not be dispatched to any rendering pass. */
  NoPass = 999,
}

/**
 * Traverses the input three.js scene and classifies all sub-objects to express
 * the rendering step they belong to. Classification is done by tagging each object
 * assigning a tag to the field 'object.userData.type'.
 *
 * The scene is traversed depth-first. The assumption here is that some models are
 * described by sub-trees of the scene. For example: the Lod point cloud is a sub.group
 * in the scene who is father of all point nodes. Also, the floorPlan may also be a
 * LodFloorPlan object whose children are the single tiles. Therefore, when a main
 * scene object is met during the scene traversal, its entire children subtree is flagged
 * in the corresponding way. Therefore, whenever a LOD object loads new children, these children
 * are automatically flagged correctly for dispatch.
 *
 * @param scene The 3js scene to process
 * @param dollhouse The dollhouse in the scene
 * @param cadModel The cad model in the scene
 * @param pointCloud The point cloud in the scene
 */
export function dispatchRenderObjects(
  scene: Object3D,
  cadModel: CadModelObject | null,
  pointCloud: PointCloudObject | null,
): void {
  const stack = [...scene.children];
  while (stack.length > 0) {
    const obj = stack.pop();
    assert(obj);
    switch (obj) {
      case cadModel:
        flagSubtree(obj, RenderDispatch.CadModelPass);
        break;
      case pointCloud:
        flagSubtree(obj, RenderDispatch.LodPointCloudPass);
        break;
      default: {
        // In order, first check whether a potential sheet object is an  orthomosaic,
        // then check whether is a non-orthomosaic sheet.
        if (
          isUnknownObject(obj) &&
          (isPanoObject(obj) ||
            isPhotoObject(obj) ||
            !!obj.userData.isOrthomosaic)
        ) {
          if (obj.userData.isPreviousImage) {
            flagSubtree(obj, RenderDispatch.PreviousImagePass);
          } else {
            flagSubtree(obj, RenderDispatch.PanoPass);
          }
          break;
        } else if (isSheetObject(obj)) {
          flagSubtree(obj, RenderDispatch.FloorPlanPass);
          // Do not push children to stack, as they are already flagged as FloorPlan,
          // otherwise their flags will be override incorrectly
          break;
        } else if (isExplorationPivot(obj)) {
          obj.userData.type = RenderDispatch.CosmeticsPass;
        } else if ("isLine2" in obj) {
          // Rectangle annotations are Line2 objects with a transparent Mesh as child
          flagSubtree(obj, RenderDispatch.CosmeticsPass);
          break;
        } else if (isObjLine(obj)) {
          obj.userData.type = RenderDispatch.CosmeticsPass;
        } else if (isObjPlaceholders(obj)) {
          obj.userData.type = RenderDispatch.OpaquePass;
        } else if (isObjOdometryPath(obj)) {
          flagSubtree(obj, RenderDispatch.CosmeticsPass);
          break;
        } else if (obj.name === BOX_CONTROLS_NAME) {
          flagSubtree(obj, RenderDispatch.CosmeticsPass);
          break;
        } else if (
          obj.name === MARKUP_MODEL_LABEL ||
          obj.name === GUIDED_NAVIGATION_GUIDES_NAME
        ) {
          flagSubtree(obj, RenderDispatch.CosmeticsPass);
          break;
        } else if (obj instanceof SplatMesh || obj instanceof SparkRenderer) {
          obj.userData.type = RenderDispatch.GaussianSplatsPass;
        } else if (obj.type === "Scene") {
          // Do not visit scenes of other views.
          obj.userData.type = RenderDispatch.NoPass;
          break;
        } else {
          switch (obj.name) {
            case GRID_PLANE_NAME:
              obj.userData.type = RenderDispatch.CosmeticsPass;
              break;
            case PREVIEW_FEATURE_NAME:
              obj.userData.type = RenderDispatch.CosmeticsPass;
              break;
            case MANUAL_BLUR_LABEL:
              obj.userData.type = RenderDispatch.CosmeticsPass;
              break;
            case ZOOM_LENS_FRAME:
              obj.userData.type = RenderDispatch.CosmeticsPass;
              break;
            default:
              if (obj instanceof Mesh && obj.material.transparent) {
                // Nothing really stable in the scene should enter here
                obj.userData.type = RenderDispatch.CosmeticsPass;
              } else {
                obj.userData.type = RenderDispatch.OpaquePass;
              }
              break;
          }
        }
        stack.push(...obj.children);
        break;
      }
    }
  }
}

/**
 *
 * @param obj The root of a 3js scene subtree
 * @param flag The flag to assign to the userData.type field of all objects in the subtree
 */
function flagSubtree(obj: Object3D, flag: RenderDispatch): void {
  obj.traverse((o) => (o.userData.type = flag));
}
